import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadInvoice, monthLabel } from "@/lib/invoice";
import { Button } from "@/components/ui/button";
import { Download, Loader2, Receipt, Wallet, CalendarDays, FileText, X } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 text-slate-500 text-sm"><Icon className={`w-4 h-4 ${accent}`} /> {label}</div>
      <div className="mt-2 text-2xl font-bold text-slate-900 font-display">{value}</div>
    </div>
  );
}

export function BillingView({ admin = false }) {
  const [data, setData] = useState({ items: [], months: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [minA, setMinA] = useState("");
  const [maxA, setMaxA] = useState("");
  const [search, setSearch] = useState("");

  const base = admin ? "/admin/billing/invoices" : "/billing/invoices";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (month) params.month = month;
      if (minA !== "") params.min_amount = Number(minA);
      if (maxA !== "") params.max_amount = Number(maxA);
      if (admin && search) params.search = search;
      const { data } = await api.get(base, { params });
      setData(data);
    } catch {
      toast.error("Could not load invoices.");
    } finally {
      setLoading(false);
    }
  }, [base, admin, month, minA, maxA, search]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setMonth(""); setMinA(""); setMaxA(""); setSearch(""); };
  const hasFilters = month || minA !== "" || maxA !== "" || search;
  const s = data.summary || {};

  return (
    <div data-testid={admin ? "admin-billing-page" : "billing-page"} className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-display text-slate-900">Billing &amp; Invoices</h1>
        <p className="text-slate-500 mt-1.5">
          {admin ? "All candidate payments and invoices across the platform." : "Your subscription payments and downloadable invoices."}
        </p>
      </div>

      {/* Summary */}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard icon={Wallet} accent="text-amber-500"
          label={admin ? "Total Revenue" : "Total Spent"} value={`₹${Number(s.total_amount || 0).toLocaleString("en-IN")}`} />
        <StatCard icon={CalendarDays} accent="text-emerald-500"
          label="This Month" value={`₹${Number(s.this_month_amount || 0).toLocaleString("en-IN")}`} />
        {admin
          ? <StatCard icon={Receipt} accent="text-sky-500" label="Paid Invoices" value={s.count || 0} />
          : <StatCard icon={Receipt} accent="text-sky-500" label="Current Plan" value={s.current_plan || "Free"} />}
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs font-semibold text-slate-500">Month</label>
          <select data-testid="filter-month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="mt-1.5 block h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500 min-w-[160px]">
            <option value="">All months</option>
            {(data.months || []).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Min Amount (₹)</label>
          <input data-testid="filter-min-amount" type="number" min="0" value={minA} onChange={(e) => setMinA(e.target.value)} placeholder="0"
            className="mt-1.5 block h-10 w-32 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Max Amount (₹)</label>
          <input data-testid="filter-max-amount" type="number" min="0" value={maxA} onChange={(e) => setMaxA(e.target.value)} placeholder="Any"
            className="mt-1.5 block h-10 w-32 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
        </div>
        {admin && (
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold text-slate-500">Search (email / invoice #)</label>
            <input data-testid="filter-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="candidate@email.com"
              className="mt-1.5 block h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
          </div>
        )}
        {hasFilters && (
          <Button data-testid="clear-filters" variant="outline" onClick={clearFilters}
            className="h-10 border-slate-300 text-slate-600 hover:bg-slate-50">
            <X className="w-4 h-4 mr-1.5" /> Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-100">
                <th className="px-6 py-3">Invoice #</th>
                <th className="px-3 py-3">Date</th>
                {admin && <th className="px-3 py-3">Candidate</th>}
                <th className="px-3 py-3">Plan</th>
                <th className="px-3 py-3">Amount</th>
                <th className="px-3 py-3">Method</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 pr-6 text-right">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={admin ? 8 : 7} className="px-6 py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" /></td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={admin ? 8 : 7} className="px-6 py-16 text-center text-slate-400">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  {hasFilters ? "No invoices match these filters." : "No invoices yet."}
                </td></tr>
              ) : data.items.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_no}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="px-6 py-3.5 font-mono text-xs font-semibold text-slate-900">{inv.invoice_no}</td>
                  <td className="px-3 py-3.5 text-slate-600">{fmtDate(inv.date)}</td>
                  {admin && <td className="px-3 py-3.5 text-slate-700">{inv.email}</td>}
                  <td className="px-3 py-3.5 text-slate-700">{inv.plan}</td>
                  <td className="px-3 py-3.5 font-semibold text-slate-900">₹{Number(inv.amount).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-3.5 text-slate-500">{inv.method}</td>
                  <td className="px-3 py-3.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" /> Paid
                    </span>
                  </td>
                  <td className="px-3 py-3.5 pr-6 text-right">
                    <button data-testid={`download-${inv.invoice_no}`} onClick={() => downloadInvoice(inv)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 text-xs font-semibold hover:bg-amber-50">
                      <Download className="w-3.5 h-3.5" /> Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Billing() {
  return <BillingView admin={false} />;
}
